import axios from 'axios';

/** Render Staging (`develop` 배포). Vercel Preview 빌드 시 기본으로 사용. */
const STAGING_API_BASE_URL = 'https://jsmarket-staging.onrender.com/api';
/** 운영 기본 API: 커스텀 도메인 장애 시에도 안정적인 Render 직접 호스트 */
const PRODUCTION_API_BASE_URL = 'https://jsmarket-ypqs.onrender.com/api';
const CUSTOM_API_BASE_URL = process.env.REACT_APP_API_URL || '';

const API_BASE_URL = (() => {
  // CRA는 Vercel의 VERCEL_ENV를 번들에 넣어주지 않는 경우가 많아서,
  // 런타임에서 vercel.app 호스트 여부로 Preview를 판별합니다.
  // 이렇게 하면 Vercel 환경변수 값이 잘못 들어가도 Preview에선 Staging API로 고정됩니다.
  const isVercelPreviewRuntime =
    typeof window !== 'undefined' && window.location.hostname.includes('vercel.app');

  if (isVercelPreviewRuntime) return STAGING_API_BASE_URL;

  // 운영 기본값은 Render 직접 호스트를 사용해 DNS/SSL 이슈를 회피한다.
  // 커스텀 도메인을 쓰고 싶으면 REACT_APP_API_URL로 명시한다.
  return CUSTOM_API_BASE_URL || PRODUCTION_API_BASE_URL;
})();

// 브라우저에서 현재 API 타겟을 빠르게 확인할 수 있도록 1회만 출력합니다.
// (필요하면 나중에 삭제 가능)
if (typeof window !== 'undefined') {
  // eslint-disable-next-line no-console
  console.log('[jsmarket] API_BASE_URL =', API_BASE_URL);
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60초 타임아웃 (이미지 업로드를 고려)
  headers: {
    'Content-Type': 'application/json',
  },
});

// 요청 인터셉터
api.interceptors.request.use(
  (config) => {
    const url = config.url || '';
    // 관리자 API(/jsadm/)는 AllowAny이며 앱 로그인 토큰과 무관해야 함.
    // 만료·무효 토큰이 붙으면 일부 환경에서 401이 나 목록이 비는 문제를 막기 위해 헤더를 보내지 않음.
    if (url.includes('/jsadm/')) {
      delete config.headers.Authorization;
      delete config.headers['X-Seller-ID'];
    } else {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Token ${token}`;
      }
    }

    // 판매자 정보가 있으면 판매자 ID를 헤더에 추가 (localStorage와 sessionStorage 모두 확인)
    const seller = localStorage.getItem('seller') || sessionStorage.getItem('seller');
    if (seller) {
      try {
        const sellerData = JSON.parse(seller);
        if (sellerData.id) {
          config.headers['X-Seller-ID'] = sellerData.id;
        }
      } catch (error) {
        // JSON 파싱 실패 시 무시
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // DNS/네트워크 장애 시 커스텀 도메인 -> Render 운영 API로 1회 폴백 재시도
    const cfg: any = error?.config;
    const hasHttpResponse = Boolean(error?.response);
    const message = String(error?.message || '');
    const shouldFallback =
      !hasHttpResponse &&
      Boolean(CUSTOM_API_BASE_URL) &&
      CUSTOM_API_BASE_URL !== PRODUCTION_API_BASE_URL &&
      cfg &&
      cfg.__fallbackRetried !== true &&
      String(cfg.baseURL || API_BASE_URL).startsWith(CUSTOM_API_BASE_URL) &&
      (message.includes('ERR_NAME_NOT_RESOLVED') ||
        message.includes('ENOTFOUND') ||
        message.includes('Network Error') ||
        message.includes('Failed to fetch'));

    if (shouldFallback) {
      cfg.__fallbackRetried = true;
      cfg.baseURL = PRODUCTION_API_BASE_URL;
      if (typeof window !== 'undefined') {
        // eslint-disable-next-line no-console
        console.warn('[jsmarket] API fallback:', CUSTOM_API_BASE_URL, '->', PRODUCTION_API_BASE_URL);
      }
      return api.request(cfg);
    }

    if (error.response?.status === 401) {
      const requestUrl: string = error.config?.url || '';
      const authExemptEndpoints = ['/users/login/', '/sellers/login/', '/auth/login/', '/jsadm/'];
      const isAuthRequest = authExemptEndpoints.some((endpoint) => requestUrl.includes(endpoint));
      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');

      if (!isAuthRequest && storedToken) {
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// API 함수들
export const authAPI = {
  login: (username: string, password: string) =>
    api.post('/auth/login/', { username, password }),
  register: (userData: any) =>
    api.post('/auth/register/', userData),
  logout: () =>
    api.post('/auth/logout/'),
  getProfile: () =>
    api.get('/users/profile/'),
  updateProfile: (userData: any) =>
    api.patch('/users/profile/', userData),
  deleteAccount: () =>
    api.delete('/users/delete-account/'),
  checkNickname: (nickname: string) =>
    api.get('/users/check-nickname/', { params: { nickname } }),
  getActivityStats: () =>
    api.get('/users/activity-stats/'),
  sendSMSCode: (phone: string) =>
    api.post('/users/send-sms-code/', { phone }),
  /** 로그인: 가입 여부 확인 + 인증번호 발송 1회 요청 (check-phone + send-sms 제거) */
  sendLoginSMS: (phone: string) =>
    api.post('/users/send-login-sms/', { phone }),
  verifySMSCode: (phone: string, code: string) =>
    api.post('/users/verify-sms-code/', { phone, code }),
  getKoreaRegions: () =>
    api.get<{ cities: string[]; districts: Record<string, string[]> }>('/users/korea-regions/'),
  checkReferrer: (payload: { referrer_public_id: string }) =>
    api.post('/users/check-referrer/', payload),
  checkJsPartner: (payload: { js_partner_public_id: string }) =>
    api.post('/users/check-js-partner/', payload),
};

export const productAPI = {
  getProducts: (params?: any) =>
    api.get('/products/', { params }),
  getProduct: (id: number, params?: any) =>
    api.get(`/products/${id}/`, { params }),
  createProduct: (productData: any) =>
    api.post('/products/', productData),
  updateProduct: (id: number, productData: any) =>
    api.patch(`/products/${id}/`, productData),
  deleteProduct: (id: number) =>
    api.delete(`/products/${id}/`),
  searchProducts: (params: any) =>
    api.get('/products/search/', { params }),
  toggleWishlist: (productId: number, payload: { buyer_phone: string }) =>
    api.post(`/products/${productId}/wishlist/`, payload),
  getWishlist: (buyerPhone: string) =>
    api.get(`/products/wishlist/${buyerPhone}/`),
  getPopularSearchTerms: () =>
    api.get('/products/popular-search-terms/'),
  getAvailableLocations: () =>
    api.get('/products/available-locations/'),
  createPurchaseReservation: (data: any) =>
    api.post('/products/purchase-reservation/', data),
  getPurchaseReservations: (buyerPhone: string) =>
    api.get(`/products/purchase-reservations/${buyerPhone}/`),
  createDirectPurchase: (data: any) =>
    api.post('/products/direct-purchase/', data),
  getDirectPurchases: (buyerPhone: string) =>
    api.get(`/products/direct-purchases/${buyerPhone}/`),
  getSellerReservations: (sellerId: number) =>
    api.get(`/products/seller-reservations/${sellerId}/`),
  updatePurchaseReservationStatus: (id: number, status: string) =>
    api.patch(`/products/purchase-reservation/${id}/`, { status }),
  updateDirectPurchaseStatus: (id: number, status: string) =>
    api.patch(`/products/direct-purchase/${id}/`, { status }),
};

// 사업자번호에서 하이픈 제거 헬퍼 함수
const cleanBusinessNumber = (businessNumber: string): string => {
  return businessNumber ? businessNumber.replace(/-/g, '') : '';
};

export const sellerAPI = {
  register: (sellerData: any) =>
    api.post('/sellers/register/', sellerData),
  getProfile: (businessNumber?: string) => {
    const params = businessNumber ? { business_number: cleanBusinessNumber(businessNumber) } : {};
    return api.get('/sellers/profile/', { params });
  },
  updateProfile: (sellerData: any, businessNumber?: string) => {
    const params = businessNumber ? { business_number: cleanBusinessNumber(businessNumber) } : {};
    return api.patch('/sellers/profile/', sellerData, { params });
  },
  deleteAccount: (businessNumber: string) => {
    return api.delete('/sellers/delete-account/', {
      data: { business_number: cleanBusinessNumber(businessNumber) }
    });
  },
  getDashboard: () =>
    api.get('/sellers/dashboard/'),
  getFees: () =>
    api.get('/sellers/fees/'),
  searchAddress: (query: string) => {
    return api.get('/sellers/search-address/', {
      params: { query },
    });
  },
};

export const visitAPI = {
  getVisits: () =>
    api.get('/visits/'),
  createVisit: (visitData: any) =>
    api.post('/visits/', visitData),
  updateVisit: (id: number, visitData: any) =>
    api.patch(`/visits/${id}/`, visitData),
  deleteVisit: (id: number) =>
    api.delete(`/visits/${id}/`),
  getDashboard: () =>
    api.get('/visits/dashboard/'),
};

export const chatAPI = {
  getConversations: () =>
    api.get('/chat/'),
  createConversation: (conversationData: any) =>
    api.post('/chat/', conversationData),
  submitAdminInquiry: (inquiryData: any, config?: any) =>
    api.post('/chat/admin-inquiries/', inquiryData, config),
  getConversation: (id: number) =>
    api.get(`/chat/${id}/`),
  getMessages: (conversationId: number, params?: { before?: number; limit?: number }) =>
    api.get(`/chat/${conversationId}/messages/`, { params }),
  sendMessage: (conversationId: number, messageData: any) =>
    api.post(`/chat/${conversationId}/messages/`, messageData),
  markAsRead: (conversationId: number) =>
    api.post(`/chat/${conversationId}/read/`),
  getDashboard: () =>
    api.get('/chat/dashboard/'),
  getUnreadCount: () =>
    api.get('/chat/unread-count/'),
};

export const notificationAPI = {
  getNotifications: () =>
    api.get('/notifications/'),
  getNotificationCount: () =>
    api.get('/notifications/count/'),
  markAsRead: (notificationId: number) =>
    api.post(`/notifications/${notificationId}/read/`),
  markAllAsRead: () =>
    api.post('/notifications/read-all/'),
};

export const adminAPI = {
  getDashboard: () =>
    api.get('/jsadm/dashboard/'),
  getInquiries: (type: 'user' | 'seller', params?: any) =>
    api.get('/jsadm/inquiries/', { params: { type, ...(params || {}) } }),
  getUsers: (params?: any) =>
    api.get('/jsadm/users/', { params }),
  deleteUser: (userId: number) =>
    api.post(`/jsadm/users/${userId}/delete/`),
  getSellers: (params?: any) =>
    api.get('/jsadm/sellers/', { params }),
  getSellerDetail: (sellerId: number) =>
    api.get(`/jsadm/sellers/${sellerId}/`),
  approveSeller: (sellerId: number) =>
    api.post(`/jsadm/sellers/${sellerId}/approve/`),
  rejectSeller: (sellerId: number) =>
    api.post(`/jsadm/sellers/${sellerId}/reject/`),
  deleteSeller: (sellerId: number) =>
    api.post(`/jsadm/sellers/${sellerId}/delete/`),
  getProductsBySeller: (params?: any) =>
    api.get('/jsadm/products-by-seller/', { params }),
  getReservations: (params?: any) => api.get('/jsadm/reservations/', { params }),
  approveReservation: (type: string, id: number) => api.post(`/jsadm/reservations/${type}/${id}/approve/`),
};
